import Modal from "@/component/Modal";
import { forwardRef, useImperativeHandle, useState } from "react";
import InputWithClear from "@/component/InputWithClear";
import IC_COLLECTOR_CLOSE from "@/assets/common/ic_collect_close.svg?react";
import VoiceModalBg from "@/assets/common/voice_modal_bg.png";
import IC_COLLECTOR_SUBMITTING from "@/assets/common/ic_collector_submitting.png";
import { speakerNameLimitPattern } from "@/constants/record";
import { IDisplayUserInfo } from "@/types/user";
import { updateUserInfo } from "@/services/api_user";
import UserAvatarUploader from "./UserAvatarUploader";
import UserAvatar from "../UserAvatar";

interface UserEditModalProps {
  userInfo: IDisplayUserInfo;
  handleCallback: (newModel: IDisplayUserInfo) => void;
}

export interface UserEditModalRef {
  open: () => void;
  close: () => void;
}

const UserEditModal = forwardRef<UserEditModalRef, UserEditModalProps>(
  (props, ref) => {
    const { userInfo, handleCallback } = props;
    const [newSpeakerName, setNewSpeakerName] = useState(userInfo.displayName);
    const [speakerNameErrMsg, setSpeakerNameErrMsg] = useState("");
    const [isSubmitting, setSubmitting] = useState(false);
    const [visible, setVisible] = useState(false);

    const onClose = () => {
      setVisible(false);
    };

    const onSpeakerNameInput = (inputValue: string) => {
      setSpeakerNameErrMsg("");
      setNewSpeakerName(inputValue);
    };

    const handleSave = async () => {
      try {
        if (!newSpeakerName?.trim()) return;
        if (newSpeakerName.trim() === userInfo.displayName) {
          setVisible(false);
          return;
        }
        setSubmitting(true);
        await updateUserInfo({ displayName: newSpeakerName.trim() });
        setSubmitting(false);
        setVisible(false);
        userInfo.displayName = newSpeakerName;
        handleCallback(userInfo);
      } catch (error) {
        setSubmitting(false);
        console.error("更新用户名称失败", error);
      }
    };

    useImperativeHandle(ref, () => ({
      open: () => {
        setVisible(true);
        setNewSpeakerName(userInfo.displayName);
      },
      close: onClose,
    }));

    return (
      <Modal visible={visible} onClose={onClose} mask>
        <div className="bg-white w-130 h-95 rounded-4 shadow relative overflow-hidden">
          <img
            src={VoiceModalBg}
            className="absolute w-full h-25 left--0.5px top--0.5px"
          />
          <div
            className="absolute right-3 top-3 cursor-pointer"
            onClick={onClose}
          >
            <IC_COLLECTOR_CLOSE className="w-6 h-6" />
          </div>
          <div className="w-full h-full flex items-center justify-center flex-col">
            <UserAvatarUploader>
              <UserAvatar
                className="w-14 h-14 text-5 font-600"
                userInfo={
                  {...userInfo, displayName: newSpeakerName}
                }
              />
            </UserAvatarUploader>
            <div className="relative mt-7 h-12 w-90">
              <InputWithClear
                allowClear
                className="w-90 rounded-2.5 pl-6 pr-10 py-3.5 border-0 bg-#ECEEF3 text-size-3.5 focus:outline-none placeholder:text-#A1A7B2 placeholder:text-size-3.5 relative"
                placeholder="请输入发言人姓名（最长10字符）"
                value={newSpeakerName}
                maxLength={10}
                limitPattern={speakerNameLimitPattern}
                onMyChange={onSpeakerNameInput}
              />
              <div className="absolute bottom--6.5 text-size-3.5 text-#FF584C">
                {speakerNameErrMsg}
              </div>
            </div>
            <div
              className={`w-90 h-12 mt-9.5 bg-#4981f2 rounded-2.5 flex items-center justify-center cursor-pointer ${
                (speakerNameErrMsg || !newSpeakerName?.trim()) && "op-30"
              }`}
              onClick={handleSave}
            >
              <span className="ml-2 text-#fff text-3.5 cursor-pointer">
                {isSubmitting ? (
                  <img
                    src={IC_COLLECTOR_SUBMITTING}
                    className="w-6 h-6 rotating-image"
                  />
                ) : (
                  "保存"
                )}
              </span>
            </div>
          </div>
        </div>
      </Modal>
    );
  }
);

export default UserEditModal;
